import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '../../i18n/useTranslation';
import { fetchWalletHistory } from '../../api/walletApi';
import { useWalletStore } from '../../store/walletStore';
import styles from './WalletHistoryModal.module.css';

interface Tx {
  id: string;
  type: 'CREDIT' | 'DEBIT';
  amount: number;
  source: string;
  description?: string;
  balanceAfter: number;
  createdAt: string;
}

interface WalletHistoryModalProps {
  token: string;
  onClose: () => void;
}

const SOURCE_LABEL: Record<string, string> = {
  INITIAL_BALANCE:  'Saldo inicial',
  SHOP_PURCHASE:    'Compra no Mercador',
  SHOP_SALE:        'Venda ao Mercador',
  FORGE_FEES:       'Forja',
  BATTLE_REWARD:    'Recompensa de batalha',
  MONSTER_DROP:     'Espólio de monstro',
  QUEST_REWARD:     'Recompensa de missão',
  ADMIN_ADJUSTMENT: 'Ajuste',
};

const PAGE = 30;

export default function WalletHistoryModal({ token, onClose }: WalletHistoryModalProps) {
  const { t } = useTranslation();
  const [txs, setTxs] = useState<Tx[]>([]);
  const [balance, setBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [skip, setSkip] = useState(0);
  const [done, setDone] = useState(false);
  const setBalanceStore = useWalletStore(s => s.setBalance);

  const load = useCallback(async (from: number) => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetchWalletHistory(token, PAGE, from);
      const batch: Tx[] = res.transactions ?? [];
      setBalance(res.balance);
      if (typeof res.balance === 'number') setBalanceStore(res.balance);
      setTxs(prev => (from === 0 ? batch : [...prev, ...batch]));
      setDone(batch.length < PAGE);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token, setBalanceStore]);

  useEffect(() => { load(0); }, [load]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const fmtDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={t('wallet.history.title')}>
        <header className={styles.header}>
          <div>
            <h2 className={styles.title}>{t('wallet.history.title')}</h2>
            <span className={styles.subtitle}>{t('wallet.history.subtitle')}</span>
          </div>
          <div className={styles.headerRight}>
            <span className={styles.chip}>💰 {(balance ?? 0).toLocaleString()} TF</span>
            <button className={styles.closeBtn} onClick={onClose} aria-label="Fechar">✕</button>
          </div>
        </header>

        <div className={styles.body}>
          {error && <div className={styles.errorBox}>{error}</div>}

          {!error && txs.length === 0 && !loading && (
            <div className={styles.empty}>{t('wallet.history.empty')}</div>
          )}

          {txs.map(tx => {
            const credit = tx.type === 'CREDIT';
            return (
              <div key={tx.id} className={styles.row}>
                <div className={styles.rowMain}>
                  <span className={styles.desc}>{tx.description || SOURCE_LABEL[tx.source] || tx.source}</span>
                  <span className={styles.meta}>
                    <span className={styles.sourceTag}>{SOURCE_LABEL[tx.source] ?? tx.source}</span>
                    {' · '}{fmtDate(tx.createdAt)}
                  </span>
                </div>
                <div className={styles.rowAmt}>
                  <span className={credit ? styles.credit : styles.debit}>
                    {credit ? '+' : '−'}{tx.amount.toLocaleString()}
                  </span>
                  <span className={styles.after}>→ {tx.balanceAfter.toLocaleString()}</span>
                </div>
              </div>
            );
          })}

          {loading && <div className={styles.center}><div className={styles.spinner} /></div>}

          {!loading && !done && txs.length > 0 && (
            <button className={styles.moreBtn} onClick={() => { const n = skip + PAGE; setSkip(n); load(n); }}>
              {t('wallet.history.more')}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
